import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { formatActorWithContext } from "@/domains/attribution/format-attribution-line";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { INoteObservable } from "@/store/note";
import { truncate } from "lodash-es";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  generateLongDateString,
  generateRecentDateString,
  generateRelativePrepositionAdverbialDateTimeString,
} from "@/domains/date/date";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSDialogHeaderContent,
  MDSDialogSlotName,
  MDSIcon,
  MDSIconKind,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSDialogChip,
  MDSDialogChipVariant,
} from "@/mds";
import { EditCreatedDatePopoverTrigger } from "./edit-created-date-popover";
import { EditCreatedTimePopoverTrigger } from "./edit-created-time-popover";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  note: INoteObservable;
  onViewVersionHistory?: () => void;
  canShowVersionHistory?: boolean;
  isShowingVersionHistory?: boolean;
}

export const AboutNoteModal = observer(function AboutNoteModal({
  isOpen,
  onClose,
  note,
  onViewVersionHistory,
  canShowVersionHistory = false,
  isShowingVersionHistory = false,
}: Props) {
  const collections = note?.collectionList?.allCollections || [];
  const hasCollections = collections.length > 0;

  const creatorName = note.attributedCreator?.profileDisplayName ?? "Unknown";
  const creatorLine = formatActorWithContext({
    actorName: creatorName,
    contextKind: note.createdContextKind,
  });

  const lastEditor = note.lastModifier ?? note.primaryOwner;
  const lastEditorName = lastEditor?.profileDisplayName ?? "Unknown";
  const lastEditedLine = formatActorWithContext({
    actorName: lastEditorName,
    contextKind: note.lastModifiedContextKind,
  });

  const handleViewVersionHistory = () => {
    onViewVersionHistory?.();
    onClose();
  };

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <StyledHeader slot={MDSDialogSlotName.Header} allowOverflow={false}>
        <MDSText variant={MDSTextVariant.HeaderL} color={MDSTextColor.Contrast}>
          About
        </MDSText>
        <MDSDialogChip
          label={truncate(note.title, { length: 50 })}
          variant={MDSDialogChipVariant.Note}
        />
      </StyledHeader>

      <StyledDialogContent slot={MDSDialogSlotName.Content}>
        <MetaDataWrapper>
          <MDSText
            variant={MDSTextVariant.ParagraphSm}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Created by
          </MDSText>
          <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
            <CreatedLine note={note} ownerName={creatorLine} />
          </MDSText>
        </MetaDataWrapper>

        <MetaData
          label="Last edited by"
          value={`${lastEditedLine} ${generateRelativePrepositionAdverbialDateTimeString(note.modifiedAt)}`}
        />

        {canShowVersionHistory && onViewVersionHistory ? (
          <ButtonRow>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={handleViewVersionHistory}
              isDisabled={isShowingVersionHistory}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.History} />
              View version history
            </MDSButton>
          </ButtonRow>
        ) : null}

        {hasCollections ? (
          <MetaData
            label="Collections"
            value={
              <ItemListRowFeaturedContent
                collections={sortCollectionsForChips(collections)}
                noteId={note.id}
                showAll
              />
            }
          />
        ) : null}
      </StyledDialogContent>
    </MDSModalDialog>
  );
});

interface CreatedLineProps {
  note: INoteObservable;
  ownerName: string;
}

const CreatedLine = observer(function CreatedLine({ note, ownerName }: CreatedLineProps) {
  // Non-owners get the original wording with time appended — preserves every
  // formatting case the modal previously showed (today / yesterday /
  // day-of-week / "on May 17" / "on 5/17/22") and adds the time to match the
  // bottom-bar mock.
  if (!note.canEditCreatedAt) {
    return (
      <>
        {ownerName} {generateRelativePrepositionAdverbialDateTimeString(note.locallyCreatedAt)}
      </>
    );
  }

  // Owners: same wording, but the formatted date and time tokens are
  // independently clickable. Recent dates keep the relative form
  // (today / yesterday / Friday); older dates use the long form
  // ("March 1, 2025") per mock 27267-86422.
  const recent = generateRecentDateString(note.locallyCreatedAt);

  switch (recent) {
    case "":
      return <>{ownerName}</>;
    case "Today":
      return (
        <>
          {ownerName} <EditCreatedDatePopoverTrigger note={note} label="today" /> at{" "}
          <EditCreatedTimePopoverTrigger note={note} />
        </>
      );
    case "Yesterday":
      return (
        <>
          {ownerName} <EditCreatedDatePopoverTrigger note={note} label="yesterday" /> at{" "}
          <EditCreatedTimePopoverTrigger note={note} />
        </>
      );
    default: {
      // Older than a week — switch from the relative form to the long form
      // ("March 1, 2025") per mocks; the previous "M/d/yy" was off-spec.
      const longDate = generateLongDateString(note.locallyCreatedAt);
      const dateLabel = /^\d/.test(recent) ? longDate : recent;
      return (
        <>
          {ownerName} on <EditCreatedDatePopoverTrigger note={note} label={dateLabel} /> at{" "}
          <EditCreatedTimePopoverTrigger note={note} />
        </>
      );
    }
  }
});

interface MetaDataProps {
  label: string;
  value: React.ReactNode;
}
const MetaData = ({ label, value }: MetaDataProps) => {
  return (
    <MetaDataWrapper>
      <MDSText
        variant={MDSTextVariant.ParagraphSm}
        weight={MDSTextWeight.Smedium}
        color={MDSTextColor.Primary}
      >
        {label}
      </MDSText>
      <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
        {value}
      </MDSText>
    </MetaDataWrapper>
  );
};

const StyledHeader = styled(MDSDialogHeaderContent)`
  padding-left: ${({ theme }) => theme.spacing[3]};
`;

const StyledDialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
`;

const MetaDataWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing[1],
}));

const ButtonRow = styled.div`
  display: flex;
  align-items: flex-start;
  // The dialog container uses gap: spacing[6] between sections; the mock shows
  // the version-history button sitting roughly half that distance below the
  // "Last edited by" line, so subtract half the gap from this row.
  margin-top: ${({ theme }) => `-${theme.spacing[3]}`};
`;
